import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "ui";

import { AiAssistantRule, EAiAssistantRuleKind } from "../../aiAgents.types";
import { useGetRules, useRuleMutations } from "../../apis";
import { ChipEditor, ChipItem } from "../ChipEditor";

import styles from "./InstructionsTab.module.scss";

interface Props {
  assistantId: string;
}

const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 2C5.6 2 2 5.6 2 10c0 1.4.4 2.7 1 3.9L2 18l4.2-1.1c1.1.6 2.4 1 3.8 1 4.4 0 8-3.6 8-8s-3.6-7.9-8-7.9z"
      fill="#22C55E"
    />
  </svg>
);

const ThumbsUp = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path
      d="M3 10v6h2v-6H3zm14-1.5h-4.5l.6-3c.1-.5-.1-1-.5-1.3-.4-.3-1-.3-1.4 0L7.5 7.5v8.5H14c.8 0 1.5-.5 1.7-1.3l1.7-5.4c.2-.7-.3-1.3-.4-1.3z"
      fill="#22C55E"
    />
  </svg>
);

const ThumbsDown = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path
      d="M3 10V4h2v6H3zm14 1.5h-4.5l.6 3c.1.5-.1 1-.5 1.3-.4.3-1 .3-1.4 0L7.5 12.5V4H14c.8 0 1.5.5 1.7 1.3l1.7 5.4c.2.7-.3 1.3-.4 1.3z"
      fill="#EF4444"
    />
  </svg>
);

const toChips = (rules: AiAssistantRule[], kind: EAiAssistantRuleKind): ChipItem[] =>
  rules
    .filter((r) => r.kind === kind)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((r) => ({ id: r.id, label: r.body }));

const InstructionsTab = ({ assistantId }: Props) => {
  const { t } = useTranslation();
  const { rules, isLoading } = useGetRules(assistantId);
  const { createRule, deleteRule } = useRuleMutations(assistantId);

  const required = useMemo(
    () => toChips(rules, EAiAssistantRuleKind.Required),
    [rules]
  );
  const boundary = useMemo(
    () => toChips(rules, EAiAssistantRuleKind.Boundary),
    [rules]
  );

  const busy = createRule.isLoading || deleteRule.isLoading;

  const handleAdd = (kind: EAiAssistantRuleKind) => (text: string) =>
    createRule.mutate({ kind, title: text.slice(0, 200), body: text });

  const handleRemove = (ruleId: string) => deleteRule.mutate({ ruleId });

  if (isLoading) {
    return (
      <div className={styles.loaderWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.serviceCard}>
        <div className={styles.serviceIcon}>
          <WhatsAppIcon />
        </div>
        <div className={styles.serviceText}>
          <div className={styles.serviceTitle}>{t("Customer Service")}</div>
          <div className={styles.serviceSub}>
            {t("Assistant dedicated to customer support")}
          </div>
        </div>
      </div>

      <ChipEditor
        variant="positive"
        title={
          <>
            <ThumbsUp />
            <span>{t("Required Behaviors")}</span>
          </>
        }
        subtitle={t("Instructions the assistant should follow")}
        placeholder={t("E.g., Be polite and professional")}
        items={required}
        disabled={busy}
        onAdd={handleAdd(EAiAssistantRuleKind.Required)}
        onRemove={handleRemove}
      />

      <ChipEditor
        variant="negative"
        title={
          <>
            <ThumbsDown />
            <span>{t("Boundaries")}</span>
          </>
        }
        subtitle={t("Actions the assistant should avoid")}
        placeholder={t("E.g., Never discuss competitors")}
        items={boundary}
        disabled={busy}
        onAdd={handleAdd(EAiAssistantRuleKind.Boundary)}
        onRemove={handleRemove}
      />
    </div>
  );
};

export default InstructionsTab;
